import { EmptyState } from '@workspace/ui/components/empty-state'
import { cn } from '@workspace/ui/lib/utils'
import { FileText } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { PdfViewer } from './pdf-viewer'

/** 16:10 — the shape held while the document's own ratio is still unknown. */
const DEFAULT_ASPECT_RATIO = 1.6
/** Leaves the sections above and below the deck visible on a laptop screen. */
const MAX_HEIGHT = '70vh'

export interface InlineDocumentPreviewProps {
  documentId: string
  className?: string
  /** Fetch presigned download URL for a document. */
  fetchUrl: (documentId: string) => Promise<string>
  /** Called when the visible page changes (navigation or initial load). */
  onPageChange?: (page: number, totalPages: number) => void
  /** Off while a modal viewer is stacked on top, so arrow keys drive only one. */
  hotkeysEnabled?: boolean
  /** 1-based page number (as a string key) → already-rendered slide image
   * URL. Passed straight through to `PdfViewer`, and also covers the gap
   * before that: the presigned download URL itself hasn't resolved yet. */
  placeholderImageUrls?: Record<string, string>
  /** Page 1's width/height ratio, known synchronously (from the same
   * already-rendered slide `placeholderImageUrls` points at) — sizes the
   * box correctly from its very first paint, before anything has loaded.
   * Without this the box holds a generic guess until pdf.js's own
   * (asynchronous) measurement arrives, and visibly reshapes when it does. */
  initialAspectRatio?: number | null
}

/**
 * A PDF rendered in the flow of the page rather than behind a click. Fixed
 * aspect box so the surrounding page keeps its vertical scroll; paging is
 * driven by the floating arrows, horizontal wheel, or swipe.
 */
export function InlineDocumentPreview({
  documentId,
  className,
  fetchUrl,
  onPageChange,
  hotkeysEnabled,
  placeholderImageUrls,
  initialAspectRatio,
}: InlineDocumentPreviewProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [isError, setIsError] = useState(false)
  // Widescreen until the document says otherwise — most decks are, and it is
  // the shape the loading state should hold so the page does not jump far.
  // `initialAspectRatio` (when known) skips the guess entirely: the lazy
  // initializer runs once, on the very first render, before anything has
  // painted — there is no frame at the wrong ratio to reshape away from.
  const [aspectRatio, setAspectRatio] = useState(() => initialAspectRatio ?? DEFAULT_ASPECT_RATIO)

  const fetchUrlRef = useRef(fetchUrl)
  fetchUrlRef.current = fetchUrl

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setIsError(false)

    fetchUrlRef
      .current(documentId)
      .then((next) => {
        if (!cancelled) setUrl(next)
      })
      .catch(() => {
        if (!cancelled) setIsError(true)
      })

    return () => {
      cancelled = true
    }
  }, [documentId])

  return (
    <div
      className={cn('mx-auto w-full overflow-hidden', className)}
      style={{
        aspectRatio,
        maxHeight: MAX_HEIGHT,
        // Pairs with maxHeight: once the height is capped, an un-capped width
        // would leave the page letterboxed inside a box wider than it can fill.
        // A portrait deck ends up a portrait box, centred by mx-auto.
        maxWidth: `calc(${MAX_HEIGHT} * ${aspectRatio})`,
      }}
    >
      {url ? (
        <PdfViewer
          url={url}
          className="size-full"
          inline
          hotkeysEnabled={hotkeysEnabled}
          onPageChange={onPageChange}
          onAspectRatio={setAspectRatio}
          placeholderImageUrls={placeholderImageUrls}
        />
      ) : !isError && placeholderImageUrls?.['1'] ? (
        // The presigned download URL hasn't resolved yet, but the deck's
        // first slide already has — show it rather than a generic spinner.
        <img
          src={placeholderImageUrls['1']}
          alt=""
          aria-hidden
          className="size-full object-contain"
        />
      ) : (
        <EmptyState
          icon={<FileText className={cn('size-12', !isError && 'animate-pulse')} />}
          title={isError ? 'Failed to load document' : 'Loading document...'}
          dashed={false}
          className={cn('size-full border-none', isError && 'text-red-400')}
        />
      )}
    </div>
  )
}
